import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Initialize auth state from localStorage
    useEffect(() => {
        const storedUser = authService.getStoredUser();
        if (storedUser && authService.getToken()) {
            setUser(storedUser);
            // Verify token is still valid
            authService.getCurrentUser()
                .then(data => {
                    if (data.success) {
                        setUser(data.user);
                        localStorage.setItem('vms_user', JSON.stringify(data.user));
                    }
                })
                .catch(() => {
                    // Token invalid, clear auth
                    authService.logout();
                    setUser(null);
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    const login = useCallback(async (username, password) => {
        try {
            setError(null);
            setLoading(true);
            const result = await authService.login(username, password);
            if (result.success) {
                setUser(result.user);
                return { success: true };
            } else {
                setError(result.message);
                return { success: false, message: result.message };
            }
        } catch (err) {
            const message = err.response?.data?.message || 'Login failed';
            setError(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        await authService.logout();
        setUser(null);
        setError(null);
    }, []);

    const isAdmin = useCallback(() => {
        return user?.role === 'admin';
    }, [user]);

    const isOperator = useCallback(() => {
        const operatorRoles = ['operator', 'admin', 'packer', 'rto'];
        return operatorRoles.includes(user?.role);
    }, [user]);

    const value = {
        user,
        loading,
        error,
        isAuthenticated: !!user,
        isAdmin,
        isOperator,
        login,
        logout,
        setError
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
